/**
 * Use-case strip — bounded walk between two bookends, drag with momentum.
 *
 * Ports src/features/Home/UseCases.tsx, which is embla with
 * `{ loop: false, align: "start", dragFree: true }` and
 * `Autoplay({ delay: 3000, stopOnInteraction: false, stopOnMouseEnter: true,
 * stopOnLastSnap: true })` plus a destroy-on-first-drag hook (D93): hover
 * pauses and resumes, a drag stops autoplay for good, page scrolling over the
 * strip changes nothing.
 *
 * Motion is embla's own model, not an approximation (D94): every frame the
 * velocity gains `displacement / duration`, is multiplied by a friction of
 * 0.68, and moves the track. A flick on release becomes a distance —
 * release velocity × 500 (mouse) or × 600 (touch) — and the strip glides to
 * it and settles, the way a feed does. Constants are copied from
 * embla-carousel 8.6.0 (`ScrollBody`, `DragHandler`, `DragTracker`).
 *
 * D95 turned the endless loop into a bounded strip. `loop` is now an attribute
 * and defaults to false:
 *
 *  - loop === false: the snap list is embla's `ScrollContain` with the default
 *    `containScroll: 'trimSnaps'` — snap 0 is the untranslated track (the
 *    `pl-8` stays visible, exactly as the reference does at rest), every later
 *    snap is a card's left edge, and every snap past `contentSize - viewSize`
 *    collapses onto that bound and is trimmed off the end of the list. The
 *    lap trick is off, autoplay stops on the last snap (`stopOnLastSnap`), and
 *    location is HARD CLAMPED to [0, last] on drag and on glide. Embla
 *    rubber-bands past the edge and springs back (`ScrollBounds`); the block
 *    refuses to leave the range at all. The difference is only ever visible
 *    while a finger is dragging past a bookend.
 *  - loop === true: unchanged from D92/D94 — the track translates left forever
 *    and any card a full lap behind the window is carried a lap forward by its
 *    own transform, so the jump is never on screen.
 *
 * The counter in the lead card is embla's `selectedScrollSnap()` clamped to
 * [1, total] and repainted when the target snap changes, which is when embla
 * emits `select` — not when the glide settles.
 */

const RESIZE_DEBOUNCE_MS = 200;

// embla ScrollBody: friction per frame, duration in frames. `duration: 25`
// is embla's default for a programmatic step; dragFree release uses 43.
const FRICTION = 0.68;
const STEP_DURATION = 25;
const FLICK_DURATION = 43;
const FRAME_MS = 1000 / 60;
const SETTLE_PX = 0.001;

// embla DragTracker: velocity is read over the last ≤170 ms of movement and
// only counts as a flick above 0.1 px/ms.
const FLICK_WINDOW_MS = 170;
const FLICK_MIN_VELOCITY = 0.1;

// embla DragHandler `freeForceBoost`: px of travel per px/ms of release velocity.
const FLICK_BOOST = { mouse: 500, touch: 600 };

// embla Engine: the tolerance ScrollContain uses to fold a snap onto a bound.
const PIXEL_TOLERANCE = 2;
const SNAP_TOLERANCE = 1;

function lapOffset( position, index, count ) {
	return Math.round( ( position - index ) / count ) * count;
}

function clamp( value, min, max ) {
	return Math.min( Math.max( value, min ), max );
}

// embla `roundToTwoDecimals` — the translate it writes is rounded, so ours is.
function roundToTwoDecimals( n ) {
	return Math.round( n * 100 ) / 100;
}

function pad( n ) {
	return String( n ).padStart( 2, '0' );
}

function initMarquee( root ) {
	const track = root.querySelector( '.card-marquee-track' );
	if ( ! track ) {
		return;
	}

	const cards = Array.from( track.children );
	if ( cards.length === 0 ) {
		return;
	}

	const count = cards.length;
	const prefersReducedMotion = window.matchMedia(
		'(prefers-reduced-motion: reduce)'
	).matches;
	const autoplayEnabled =
		root.dataset.autoplay !== 'false' && ! prefersReducedMotion;
	const delay = parseInt( root.dataset.autoplayDelay, 10 ) || 3000;
	const pauseOnHover = root.dataset.pauseOnHover !== 'false';
	const stopOnInteractionEnabled = root.dataset.stopOnInteraction !== 'false';
	const loopEnabled = root.dataset.loop === 'true';

	// The counter counts the CASES, not the cards: the two bookends are not
	// use cases. `data-total` carries the number save() wrote into `01 / NN`.
	const total = parseInt( root.dataset.total, 10 ) || count;

	// `stride` is one card plus one gap; `startX` is the track's own left
	// padding. With loop the reference cancels it out (embla's first snap is
	// `-startX`); bounded, embla clamps the first snap to 0 and the padding
	// stays on screen, so `origin` is 0.
	let stride = 0;
	let startX = 0;
	let viewSize = 0;
	let contentSize = 0;
	let maxLocation = 0;
	let snapLocations = [ 0 ];

	/**
	 * embla `ScrollSnaps` + `ScrollContain` with `containScroll: 'trimSnaps'`,
	 * expressed in this file's positive travel space (embla's snaps are
	 * negative: `snapLocation = -scrollSnap`).
	 *
	 * @param {number[]} offsets Each card's left edge from the track's left edge, in px.
	 */
	function computeSnapLocations( offsets ) {
		if ( contentSize <= viewSize + PIXEL_TOLERANCE ) {
			return [ 0 ];
		}
		const bounded = offsets.map( ( offset, index ) => {
			if ( index === 0 ) {
				return 0;
			}
			if ( index === offsets.length - 1 ) {
				return maxLocation;
			}
			const snap = clamp( offset, 0, maxLocation );
			if ( Math.abs( maxLocation - snap ) <= SNAP_TOLERANCE ) {
				return maxLocation;
			}
			if ( Math.abs( snap ) <= SNAP_TOLERANCE ) {
				return 0;
			}
			return parseFloat( snap.toFixed( 3 ) );
		} );
		// embla `findScrollContainLimit`: drop the repeats of the first and of
		// the last snap, so the strip has exactly one resting place per edge.
		const first = bounded[ 0 ];
		const last = bounded[ bounded.length - 1 ];
		return bounded.slice(
			bounded.lastIndexOf( first ),
			bounded.indexOf( last ) + 1
		);
	}

	function measure() {
		// Rect deltas, not offsetLeft: both rects carry the same transform, so
		// their difference is the untransformed geometry whatever the track is
		// translated to, and it does not depend on which ancestor happens to be
		// the offsetParent.
		const trackLeft = track.getBoundingClientRect().left;
		const offsets = cards.map(
			( card ) => card.getBoundingClientRect().left - trackLeft
		);
		startX = offsets[ 0 ];
		stride = count > 1 ? offsets[ 1 ] - offsets[ 0 ] : 0;
		viewSize = track.offsetWidth;
		contentSize =
			cards[ count - 1 ].getBoundingClientRect().right - trackLeft;
		maxLocation = Math.max( contentSize - viewSize, 0 );
		snapLocations = computeSnapLocations( offsets );
	}

	measure();
	if ( stride <= 0 ) {
		return;
	}

	const counterEl = root.querySelector( '[data-use-cases-counter]' );

	function clampLocation( px ) {
		return loopEnabled ? px : clamp( px, 0, maxLocation );
	}

	function nearestSnapIndex( px ) {
		let best = 0;
		let bestDistance = Infinity;
		snapLocations.forEach( ( snap, index ) => {
			const distance = Math.abs( snap - px );
			if ( distance < bestDistance ) {
				bestDistance = distance;
				best = index;
			}
		} );
		return best;
	}

	// `location` is where the track is right now, in px of travel — fractional
	// while dragging or gliding. `target` is where autoplay is counting from,
	// in whole cards, so a tick that lands mid-glide cannot drift. Bounded, the
	// same job is done by `snapIndex` into the trimmed snap list.
	let location = 0;
	let target = 0;
	let snapIndex = 0;

	function paintCounter() {
		if ( ! counterEl ) {
			return;
		}
		counterEl.textContent = pad( clamp( snapIndex, 1, total ) );
	}

	function render( px ) {
		if ( loopEnabled ) {
			const position = px / stride;
			cards.forEach( ( card, index ) => {
				const lap = lapOffset( position, index, count );
				card.style.transform = lap
					? `translateX(${ lap * stride }px)`
					: '';
			} );
			track.style.transform = `translateX(${ roundToTwoDecimals(
				-( startX + px )
			) }px)`;
			return;
		}
		track.style.transform = `translateX(${ roundToTwoDecimals( -px ) }px)`;
	}

	// ── Scroll body + animation loop (embla `ScrollBody` + `Animations`) ──
	//
	// `location` is where the physics has got to; `offsetLocation` is what is
	// on screen — embla renders the frame INTERPOLATED between the last two
	// physics steps by the fraction of a timestep the frame is into the future
	// (`alpha`), which is why its transform lands on values the 60 Hz steps
	// never visit. Without that interpolation the block sat ~0.5 px off the
	// reference on every mid-glide sample.

	let animationId = 0;
	let velocity = 0;
	let targetPx = 0;
	let duration = STEP_DURATION;
	let previousLocation = 0;
	let offsetLocation = 0;
	let lastTimeStamp = null;
	let accumulatedTime = 0;

	function stopAnimation() {
		window.cancelAnimationFrame( animationId );
		lastTimeStamp = null;
		accumulatedTime = 0;
		animationId = 0;
	}

	function stopGlide() {
		stopAnimation();
		velocity = 0;
	}

	function seek() {
		const displacement = targetPx - location;
		previousLocation = location;
		velocity += displacement / duration;
		velocity *= FRICTION;
		location += velocity;
	}

	function settled() {
		return Math.abs( targetPx - offsetLocation ) < SETTLE_PX;
	}

	function animate( timeStamp ) {
		if ( ! animationId ) {
			return;
		}
		// embla runs the physics twice on the very first frame of a scroll,
		// before any time has elapsed.
		if ( lastTimeStamp === null ) {
			lastTimeStamp = timeStamp;
			seek();
			seek();
		}
		const timeElapsed = timeStamp - lastTimeStamp;
		lastTimeStamp = timeStamp;
		accumulatedTime += timeElapsed;
		while ( accumulatedTime >= FRAME_MS ) {
			seek();
			accumulatedTime -= FRAME_MS;
		}
		const alpha = accumulatedTime / FRAME_MS;
		const hasSettled = settled() && ! isDragging;
		if ( hasSettled ) {
			stopAnimation();
		}
		offsetLocation = location * alpha + previousLocation * ( 1 - alpha );
		render( offsetLocation );
		if ( animationId ) {
			animationId = window.requestAnimationFrame( animate );
		}
	}

	function startAnimation() {
		if ( animationId ) {
			return;
		}
		animationId = window.requestAnimationFrame( animate );
	}

	function glideTo( px, frames ) {
		targetPx = clampLocation( px );
		duration = frames;
		startAnimation();
	}

	// ── Autoplay ──────────────────────────────────────────────────────────

	let permanentlyStopped = false;
	let hovering = false;
	let isDragging = false;
	let autoplayTimerId = null;

	function step() {
		if ( permanentlyStopped || hovering || isDragging ) {
			return;
		}
		if ( ! loopEnabled ) {
			// embla Autoplay `stopOnLastSnap`: the plugin stops once the strip
			// cannot scroll any further, it does not wrap back to the start.
			if ( snapIndex >= snapLocations.length - 1 ) {
				stopAutoplay();
				return;
			}
			snapIndex += 1;
			paintCounter();
			glideTo( snapLocations[ snapIndex ], STEP_DURATION );
			return;
		}
		target += 1;
		glideTo( target * stride, STEP_DURATION );
	}

	function startAutoplay() {
		if ( ! autoplayEnabled || permanentlyStopped ) {
			return;
		}
		stopAutoplay();
		autoplayTimerId = setInterval( step, delay );
	}

	function stopAutoplay() {
		if ( autoplayTimerId ) {
			clearInterval( autoplayTimerId );
			autoplayTimerId = null;
		}
	}

	function markInteracted() {
		if ( stopOnInteractionEnabled ) {
			permanentlyStopped = true;
			stopAutoplay();
		}
	}

	if ( pauseOnHover ) {
		root.addEventListener( 'mouseenter', () => {
			hovering = true;
		} );
		root.addEventListener( 'mouseleave', () => {
			hovering = false;
		} );
	}

	// ── Drag (embla `DragHandler` + `DragTracker`, dragFree) ──────────────
	//
	// Pointer events cover mouse and touch alike now that the track is no
	// longer a native scroller. `touch-action: pan-y` in style.scss keeps
	// vertical page scrolling with the browser.

	let dragStartX = 0;
	let dragStartLocation = 0;
	let dragPointerType = 'mouse';
	// Velocity window: the sample the window started at and the latest one.
	let windowStart = null;
	let windowLast = null;

	track.addEventListener( 'pointerdown', ( event ) => {
		if ( event.button !== 0 && event.pointerType === 'mouse' ) {
			return;
		}
		stopGlide();
		markInteracted();
		isDragging = true;
		dragPointerType = event.pointerType === 'mouse' ? 'mouse' : 'touch';
		dragStartX = event.clientX;
		dragStartLocation = location;
		windowStart = { x: event.clientX, t: event.timeStamp };
		windowLast = windowStart;
		track.setPointerCapture( event.pointerId );
		track.classList.add( 'is-dragging' );
	} );

	track.addEventListener( 'pointermove', ( event ) => {
		if ( ! isDragging ) {
			return;
		}
		const sample = { x: event.clientX, t: event.timeStamp };
		if ( sample.t - windowStart.t > FLICK_WINDOW_MS ) {
			windowStart = windowLast;
		}
		windowLast = sample;
		// dragFree: the strip follows the finger 1:1, up to the bookends.
		location = clampLocation(
			dragStartLocation - ( event.clientX - dragStartX )
		);
		previousLocation = location;
		offsetLocation = location;
		render( location );
	} );

	function releaseVelocity( event ) {
		if ( ! windowStart || ! windowLast ) {
			return 0;
		}
		const dx = windowLast.x - windowStart.x;
		const dt = event.timeStamp - windowStart.t;
		const expired = event.timeStamp - windowLast.t > FLICK_WINDOW_MS;
		if ( ! dt || expired ) {
			return 0;
		}
		const v = dx / dt;
		return Math.abs( v ) > FLICK_MIN_VELOCITY ? v : 0;
	}

	function endDrag( event ) {
		if ( ! isDragging ) {
			return;
		}
		isDragging = false;
		track.classList.remove( 'is-dragging' );
		// A flick keeps the strip moving: release velocity becomes a distance
		// and the scroll body glides there. No flick — it stays put (dragFree).
		const force =
			-releaseVelocity( event ) * FLICK_BOOST[ dragPointerType ];
		const destination = clampLocation( location + force );
		if ( force ) {
			glideTo( destination, FLICK_DURATION );
		}
		if ( loopEnabled ) {
			target = Math.round( destination / stride );
			return;
		}
		// embla emits `select` on pointerUp with the snap nearest the GLIDE
		// DESTINATION, not the release point, so the counter jumps at once.
		snapIndex = nearestSnapIndex( destination );
		paintCounter();
	}

	track.addEventListener( 'pointerup', endDrag );
	track.addEventListener( 'pointercancel', endDrag );

	// No `wheel` handler on purpose (D93): the track is not a scroller, so a
	// wheel event here is the reader scrolling the PAGE with the cursor over
	// the cards. Treating it as an interaction stopped autoplay for good on
	// the first trackpad flick — the strip looked dead on every visit.

	let resizeTimeoutId = null;
	window.addEventListener( 'resize', () => {
		clearTimeout( resizeTimeoutId );
		resizeTimeoutId = setTimeout( () => {
			// Keep the same card position across the re-measure: stride may
			// have changed, so px of travel is re-derived from cards.
			const position = location / stride;
			measure();
			if ( loopEnabled ) {
				location = position * stride;
				targetPx = Math.round( targetPx / stride ) * stride;
			} else {
				// Bounded, the snap list itself may have gained or lost
				// entries between the two widths.
				snapIndex = clamp( snapIndex, 0, snapLocations.length - 1 );
				location = snapLocations[ snapIndex ];
				targetPx = location;
				paintCounter();
			}
			previousLocation = location;
			offsetLocation = location;
			render( location );
		}, RESIZE_DEBOUNCE_MS );
	} );

	render( location );
	paintCounter();
	startAutoplay();
}

document.addEventListener( 'DOMContentLoaded', () => {
	document
		.querySelectorAll( '.wp-block-avoid-content-card-marquee' )
		.forEach( initMarquee );
} );
